import type { FC } from "react";
import React, { useEffect, useState } from "react";
import { Table, Checkbox } from "flowbite-react";
import { Button } from "@components";
import { ITableData } from "../../types";
import isSmallScreen from "../../helpers/is-small-screen";
import { tableHeadCell } from "@design-system";

// Stil for å sikre at ikoner er 16x16 piksler
const iconStyle = `
  .table-action-icon svg {
    width: 16px !important;
    height: 16px !important;
  }
  
  @media (max-width: 640px) {
    .responsive-table th, .responsive-table td {
      padding-left: 0.5rem !important;
      padding-right: 0.5rem !important;
    }
    .responsive-table .action-cell {
      padding-left: 0.25rem !important;
      padding-right: 0.25rem !important;
    }
  }
`;

interface StandardTableProps extends ITableData {
  compact?: boolean;
}

export const StandardTable: FC<StandardTableProps> = function (
  props: StandardTableProps,
) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Sjekk om skjermen er mobil ved lasting
    setIsMobile(window.innerWidth < 640);

    // Legg til event listener for å oppdatere ved endring av skjermstørrelse
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      <style>{iconStyle}</style>
      <div className="overflow-x-auto">
        <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600 rounded-lg overflow-hidden responsive-table">
          <Table.Head>
            {props.canMultiSelect && !isSmallScreen() ? (
              <Table.HeadCell className={tableHeadCell}>
                <Checkbox
                  className="hover:cursor-pointer checked:bg-lgb-primary focus:ring-brand dark:checked:bg-lgb-primary dark:focus:ring-brand"
                  id="select-all"
                  name="select-all"
                />
              </Table.HeadCell>
            ) : null}
            {props.header.map((header) => (
              <Table.HeadCell
                className={`${tableHeadCell} ${header.classOverride ?? ""}`}
                key={header.id}
                hidden={
                  header.hidden ||
                  (isSmallScreen() && !header.showOnSmallScreen)
                }
              >
                {header.children}
              </Table.HeadCell>
            ))}
            {props.hasActions ? (
              <Table.HeadCell className={`${tableHeadCell} action-cell`}></Table.HeadCell>
            ) : null}
          </Table.Head>
          <Table.Body className="divide-y divide-border bg-surface dark:divide-border">
            {props.rows.map((row, index) => {
              if (props.shouldRenderRow && !props.shouldRenderRow(row)) {
                return null;
              }
              return (
                <Table.Row
                  key={row.id}
                  className={`
                    ${index % 2 === 0 ? "bg-surface dark:bg-surface" : "bg-surface-muted dark:bg-surface-muted/50"}
                    ${
                      row.isActive
                        ? "bg-brand-muted dark:bg-lgb-blue-400 dark:bg-opacity-30 hover:cursor-pointer"
                        : props.onRowClick
                          ? "hover:bg-brand-muted dark:hover:bg-lgb-blue-400 dark:hover:bg-opacity-30 hover:cursor-pointer"
                          : ""
                    }
                  `}
                  onClick={() => props.onRowClick?.(row.onRowClickData)}
                >
                  {props.canMultiSelect && !isSmallScreen() ? (
                    <Table.Cell
                      className={`w-4 ${props.compact ? "py-1" : "py-2"} px-3`}
                    >
                      <div className="flex items-center">
                        <Checkbox
                          className="hover:cursor-pointer checked:bg-lgb-primary focus:ring-brand dark:checked:bg-lgb-primary dark:focus:ring-brand"
                          aria-describedby="checkbox-1"
                          id={`checkbox-${row.id}`}
                        />
                      </div>
                    </Table.Cell>
                  ) : null}
                  {row.cells.map((cell) => (
                    <Table.Cell
                      key={cell.id}
                      hidden={
                        cell.hidden ||
                        (isSmallScreen() && !cell.showOnSmallScreen)
                      }
                      className={
                        cell.classOverride
                          ? cell.classOverride
                          : props.compact
                            ? "whitespace-nowrap py-1 px-3 text-sm sm:text-base font-normal text-text"
                            : "whitespace-nowrap py-2 px-3 text-sm sm:text-base font-normal text-text"
                      }
                    >
                      {cell.children}
                    </Table.Cell>
                  ))}
                  {props.hasActions && row.actions ? (
                    <Table.Cell
                      className={`w-4 text-right ${props.compact ? "py-1" : "py-2"} px-3 text-sm sm:text-base font-medium text-text action-cell`}
                    >
                      <div className="flex items-center justify-end">
                        {row.actions.map((action) =>
                          action.children ? (
                            action.children
                          ) : (
                            <Button
                              key={action.id}
                              className="w-8 h-8 sm:w-10 sm:h-10 mx-0.5 sm:mx-1 p-0 flex items-center justify-center bg-transparent hover:bg-transparent active:bg-transparent focus:bg-transparent focus:ring-0 border-0 shadow-none"
                              color="none"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                action.onActionClick && action.onActionClick(e);
                              }}
                            >
                              <span className="table-action-icon flex items-center justify-center text-secondary dark:text-white hover:text-brand dark:hover:text-lgb-blue-200 hover:opacity-80 w-full h-full">
                                {action.icon}
                              </span>
                            </Button>
                          ),
                        )}
                      </div>
                    </Table.Cell>
                  ) : null}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    </>
  );
};
